import { useEffect, useRef, useState } from 'react';
import './ScaleFrame.css';

const FRAME_WIDTH = 1512;

/*
  Nav pins its logo at left:24px and its rightmost link at right:60px (see
  Nav.css) — those are the tightest elements to the frame's edges anywhere in
  the app. Capping the crop below that keeps them on-screen even when a page
  is scaled up past its width-fit size.
*/
const MAX_CROP_UNSCALED = 20;

/*
  The whole page is built with absolute-positioned pixel values matching a
  fixed 1512px Figma frame (see build plan Phase D, not yet done). Rather
  than rewrite every component's CSS for fluid reflow, this scales the
  entire frame to match the viewport width so nothing requires horizontal
  scrolling. Self-contained: delete this wrapper (and its use in App.js) to
  swap in real responsive breakpoints later without touching any section.

  On viewports much taller than they are wide, a short page (e.g. Contact
  Us) scaled to fit width alone can end shorter than the viewport, leaving a
  gap below it. To close that, scale is also driven by height — capped so it
  never crops the frame's edges past MAX_CROP_UNSCALED, which keeps Nav's
  logo/links on-screen. A short page on a very tall/narrow viewport can still
  fall short of full height within that cap; ScaleFrame.css's background
  covers whatever gap remains instead of exposing the page background.
*/
function ScaleFrame({ children }) {
  const innerRef = useRef(null);
  const [layout, setLayout] = useState({ scale: 1, offsetX: 0, frameHeight: 0 });

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return undefined;

    function recompute() {
      const contentHeight = inner.scrollHeight;
      const widthScale = window.innerWidth / FRAME_WIDTH;
      const heightScale = contentHeight > 0 ? window.innerHeight / contentHeight : 0;
      const scaleCap = window.innerWidth / (FRAME_WIDTH - 2 * MAX_CROP_UNSCALED);
      const scale = Math.max(widthScale, Math.min(heightScale, scaleCap));

      const frameHeight = Math.max(contentHeight * scale, window.innerHeight);

      setLayout({
        scale,
        offsetX: (window.innerWidth - FRAME_WIDTH * scale) / 2,
        frameHeight,
        /*
          The unscaled height a section needs to be for its scaled-down size
          to exactly fill frameHeight. Equal to contentHeight when width
          drives the scale (the common case — nothing changes for a section
          that doesn't opt in below). When height is capped short of what's
          needed to fill the viewport (see MAX_CROP_UNSCALED above), this is
          larger than contentHeight, so a section can read it via
          `var(--frame-target-height)` to stretch itself the rest of the way
          instead of leaving ScaleFrame's own background exposed. Feeds back
          through the ResizeObserver below into a stable fixed point rather
          than a runaway loop: growing to this exact value makes the next
          measured heightScale equal scaleCap, which reproduces the same
          scale and target height on the next pass.
        */
        targetHeight: frameHeight / scale,
      });

      /*
        Chromium can leave the SVG textPath headings (Nav, ContactUs,
        OurApp's arced titles) rasterized at their pre-resize size/position
        for a beat after the browser DevTools device toolbar swaps viewport
        + device-pixel-ratio together — the new `transform: scale(...)` is
        already correct by the time this reads back, but the compositor's
        cached paint of the text layers hasn't been thrown out yet, so it
        looks stretched/misplaced until something (a click, a scroll) forces
        a repaint. A plain window resize (dragging the browser edge) doesn't
        trigger this — only a device-pixel-ratio change alongside it does.
        Forcing a synchronous reflow via a display toggle discards that
        stale paint immediately instead of waiting on user interaction.
        Scheduled on the next frame so it runs after the browser has
        applied the transform above from this same recompute() call.
      */
      requestAnimationFrame(() => {
        const el = innerRef.current;
        if (!el) return;
        const prevDisplay = el.style.display;
        el.style.display = 'none';
        void el.offsetHeight;
        el.style.display = prevDisplay;
      });
    }

    recompute();
    window.addEventListener('resize', recompute);

    /*
      Content height also changes without the window resizing — images
      finishing, or a fetch swapping a loading message for a grid of cards.
      Measuring only on mount left the wrapper at its first height and clipped
      anything that grew past it (the Recipes pagination and footer sat below
      the cutoff and couldn't be reached).
    */
    const observer = new ResizeObserver(recompute);
    observer.observe(inner);

    return () => {
      window.removeEventListener('resize', recompute);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="ScaleFrame" style={{ height: layout.frameHeight }}>
      <div
        className="ScaleFrame-inner"
        ref={innerRef}
        style={{
          transform: `scale(${layout.scale})`,
          left: layout.offsetX,
          '--frame-target-height': `${layout.targetHeight || 0}px`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default ScaleFrame;
